import React, { useEffect, useState, useRef, useCallback } from "react";
import { useAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { Table, Button, Form, Input, Flex } from "antd";
import {
  DeleteRowOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PageListActionBar from "../../../../components/layout/page.actionBar";
import { caseTableColumns } from "../table.columns";
import { useDeleteCase } from "../../../../hooks/cpd";
import { useNotification } from "../../../../components/notification.provider";
import { cpdContentCases } from "../../../../services/queries";
import { cpdContentCasesFilterState } from "../../../../state";
import { EmptyDataPane } from "../nodes/node";
import Scenario from "./scenario";

export default function CpdCaseListPage({
  listItems,
  loading,
  refetch,
  setQueryParamFilter,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(Form.useForm());
  const [pageSize, setPageSize] = useState(10);
  const [casesFilters, setCasesFilters] = useAtom(cpdContentCasesFilterState);
  const resetFilters = useResetAtom(cpdContentCasesFilterState);

  const [selectedCaseKeys, setSelectedCaseKeys] = useState();

  const searchRef = useRef(null);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, [casesFilters.searchText]);

  useEffect(() => {
    setSelectedCaseKeys(casesFilters.selectedCases.map((elem) => elem.uid));
  }, [casesFilters.selectedCases]);

  const notificationApi = useNotification();

  const caseDeleteMutation = useDeleteCase();

  useEffect(() => {
    refetch();
  }, [listItems]);

  useEffect(() => setPageSize(pageSize), [pageSize]);

  const rowSelectionConfig = {
    onChange: (selectedRowKeys, selectedRows) => {
      setCasesFilters((prev) => {
        return {
          ...prev,
          selectedCases: selectedRows,
        };
      });
    },
    selectedRowKeys: selectedCaseKeys,
    getCheckboxProps: (record) => ({
      // disabled: record.name === '',
      name: record.name,
    }),
    hideSelectAll: true,
  };

  const paginationConfig = {
    pageSize: pageSize,
    // showSizeChanger: true,
    // onShowSizeChange: (size) => console.log(size),
  };

  const handleViewCase = (_case) => {
    navigate(`/manage/cpd/cases/${_case?.uid}/details`, {
      // pass case list too, used in CREATE
      // or UPDATE views to load dropdown of case themes
      state: { case: _case, elements: listItems },
    });
  };

  const handleDeleteCase = async (caseObject) => {
    // deleting only one caseObject
    const { uid: caseUid } = caseObject;

    caseDeleteMutation.mutate(caseUid);
    refetch();
  };

  const handleDeleteMultipleCases = async (casesObject) => {};

  const handleRefreshPage = () => {
    resetFilters();
    setQueryParamFilter();
    refetch();
  };

  return (
    <React.Fragment>
      <PageListActionBar
        form={form}
        setForm={setForm}
        leftElement={() => (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {pageSize > 5 && (
              <Input.Search
                ref={searchRef}
                placeholder="Search cases"
                allowClear
                value={casesFilters.searchText}
                onChange={(e) => {
                  const value = e.target.value;
                  setCasesFilters((prev) => {
                    return {
                      ...prev,
                      searchText: value,
                    };
                  });
                }}
                enterButton={false}
                size="middle"
                // value={searchText}
                style={{ width: 300 }}
                onSearch={(value) =>
                  setQueryParamFilter((prev) => {
                    return {
                      ...prev,
                      title: value,
                      themeTitle: value,
                      scenario: value,
                    };
                  })  
                }
              />
            )}
            <Button
              title="Refresh"
              onClick={handleRefreshPage}
              icon={<ReloadOutlined />}
              size="middle"
              type="dashed"
            />
          </div>
        )}
        rightElement={() => (
          <Flex wrap gap="small">
            <Button
              type="dashed"
              onClick={() =>
                navigate("/manage/cpd/cases/add-new-case", {
                  state: { cases: listItems }, // used in CREATE_NEW_VIEW to load dropdown of case themes
                })
              }
              icon={<PlusOutlined />}
            >
              Add new case
            </Button>
            {casesFilters.selectedCases.length >= 1 && (
              <Button
                type="dashed"
                icon={<DeleteRowOutlined />}
                color="volcano"
                variant="solid"
                onClick={() => {
                  casesFilters.selectedCases.forEach((caseRecord) =>
                    handleDeleteCase(caseRecord)
                  );
                  resetFilters();
                }}
              >
                Delete selected
              </Button>
            )}
          </Flex>
        )}
      />
      <Table
        loading={loading}
        columns={caseTableColumns({
          handleViewCase,
          handleDeleteCase: (caseObject) => handleDeleteCase(caseObject),
          notificationApi,
        })}
        dataSource={listItems}
        rowKey="uid"
        rowSelection={rowSelectionConfig}
        pagination={paginationConfig}
        showSorterTooltip={{ target: "sorter-icon" }}
        locale={{
          emptyText: (
            <EmptyDataPane textMessage="No cases match provided filter criteria" />
          ),
        }}
      />
    </React.Fragment>
  );
}
